"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const EtudeError_1 = require("../error/EtudeError");
const EtudeParser_1 = require("../util/EtudeParser");
const Exceptional_1 = require("../util/Exceptional");
const MathUtil_1 = require("../util/MathUtil");
const Accidental_1 = require("./Accidental");
const Interval_1 = require("./Interval");
const Key_1 = require("./Key");
const Letter_1 = require("./Letter");
const KeySignature_1 = require("./KeySignature");
const MusicConstants_1 = require("./MusicConstants");
const Policy_1 = require("./Policy");
class Pitch {
    constructor(key, octave) {
        this.key = key;
        this.octave = octave;
        const programNumber = this.getProgramNumber();
        if (programNumber < MusicConstants_1.default.SMALLEST_PROGRAM_NUMBER || programNumber > MusicConstants_1.default.LARGEST_PROGRAM_NUMBER) {
            throw EtudeError_1.default.forInvalid(Pitch, programNumber, "out of program number range");
        }
    }
    apply(keySignature) {
        const key = this.key.apply(keySignature);
        if (this.key === key) {
            return this;
        }
        return new Pitch(key, this.octave);
    }
    step(amount, policies = Policy_1.default.DEFAULT_PRIORITY) {
        if (amount instanceof Interval_1.default) {
            if (arguments.length !== 1) {
                throw EtudeError_1.default.forIllegalArgument(Letter_1.default, amount, "Invalid number of arguments");
            }
            const interval = amount;
            // determine the letter
            const letters = Letter_1.default.getLetters(this.key.getLetter());
            const letter = letters[MathUtil_1.default.floorMod(interval.getDistance() - 1, Letter_1.default.size)];
            // initialize accidental to be the accidental of the new letter in the key signature of this key
            let accidental = new Key_1.default(letter).apply(KeySignature_1.default.fromKey(this.key, KeySignature_1.default.Quality.MAJOR)).getAccidental();
            const accidentalOffset = accidental.map(a => a.getOffset()).orElse(0);
            // change accidental based on interval's quality
            switch (interval.getQuality()) {
                case Interval_1.default.Quality.PERFECT:
                case Interval_1.default.Quality.MAJOR:
                    break;
                case Interval_1.default.Quality.MINOR:
                    accidental = Accidental_1.default.fromOffset(accidentalOffset - 1);
                    break;
                case Interval_1.default.Quality.DIMINISHED:
                    accidental = Accidental_1.default.fromOffset(accidentalOffset - (Interval_1.default.isPerfect(interval.getDistance()) ? 1 : 2));
                    break;
                case Interval_1.default.Quality.DOUBLY_DIMINISHED:
                    accidental = Accidental_1.default.fromOffset(accidentalOffset - (Interval_1.default.isPerfect(interval.getDistance()) ? 2 : 3));
                    break;
                case Interval_1.default.Quality.AUGMENTED:
                    accidental = Accidental_1.default.fromOffset(accidentalOffset + 1);
                    break;
                case Interval_1.default.Quality.DOUBLY_AUGMENTED:
                    accidental = Accidental_1.default.fromOffset(accidentalOffset + 2);
                    break;
            }
            // prefer null over natural
            accidental = accidental.filter(a => a !== Accidental_1.default.NATURAL);
            // refer to Interval.between for how this equation was derived
            const octaveOffset = Math.trunc((interval.getDistance()
                - 1
                + (MathUtil_1.default.floorMod(this.getKey().getLetter().ordinal() - 2, Letter_1.default.size) - MathUtil_1.default.floorMod(letter.ordinal() - 2, Letter_1.default.size))) / Letter_1.default.size);
            return Exceptional_1.default.of(new Pitch(new Key_1.default(letter, accidental), this.octave + octaveOffset));
        }
        else if (typeof amount === "number") {
            return Pitch.fromProgramNumber(this.getProgramNumber() + amount, policies);
        }
    }
    halfStepUp(policies = Policy_1.default.DEFAULT_PRIORITY) {
        return Pitch.fromProgramNumber(this.getProgramNumber() + 1, policies);
    }
    halfStepDown(policies = Policy_1.default.DEFAULT_PRIORITY) {
        return Pitch.fromProgramNumber(this.getProgramNumber() - 1, policies);
    }
    getHigherPitch(key) {
        // TODO: remove try catch
        try {
            let pitch = new Pitch(key, this.octave);
            // should never loop more than twice
            while (!pitch.isHigherThan(this)) {
                pitch = new Pitch(key, pitch.getOctave() + 1);
            }
            return Exceptional_1.default.of(pitch);
        }
        catch (e) {
            return Exceptional_1.default.empty();
        }
    }
    getLowerPitch(key) {
        // TODO: remove try catch
        try {
            let pitch = new Pitch(key, this.octave);
            // should never loop more than twice
            while (!pitch.isLowerThan(this)) {
                pitch = new Pitch(key, pitch.getOctave() - 1);
            }
            return Exceptional_1.default.of(pitch);
        }
        catch (e) {
            return Exceptional_1.default.empty();
        }
    }
    isHigherThan(pitch) {
        return this.getProgramNumber() > pitch.getProgramNumber();
    }
    isLowerThan(pitch) {
        return this.getProgramNumber() < pitch.getProgramNumber();
    }
    compareTo(pitch) {
        return MathUtil_1.default.compare(this.getProgramNumber(), pitch.getProgramNumber());
    }
    static isEnharmonic(a, b) {
        return a.getProgramNumber() === b.getProgramNumber();
    }
    static fromProgramNumber(programNumber, policies = Policy_1.default.DEFAULT_PRIORITY) {
        return EtudeParser_1.default
            .of(programNumber)
            .filter(p => MusicConstants_1.default.SMALLEST_PROGRAM_NUMBER <= p && p <= MusicConstants_1.default.LARGEST_PROGRAM_NUMBER, EtudeError_1.default.forInvalid(Pitch, programNumber, "out of range"))
            .parse(p => {
            const key = Key_1.default.fromOffset(MathUtil_1.default.floorMod(p, MusicConstants_1.default.KEYS_IN_OCTAVE), policies);
            if (!key.isPresent()) {
                return Exceptional_1.default.empty(key.getException());
            }
            const actualKey = key.get();
            let octave = Math.trunc(p / MusicConstants_1.default.KEYS_IN_OCTAVE);
            /**
            * Key offsets are bounded by the range [0, MusicConstants.KEYS_IN_OCTAVE) whereas program numbers go across octave boundaries.
            * If [actual key offset] is equal to [offset after normalizing], then octave is not changed.
            * If [actual key offset] is lower than [offset after normalizing], then octave is raised by 1.
            * If [actual key offset] is higher than [offset after normalizing], then octave is lowered by 1.
            */
            octave += (actualKey.getOffset() - (actualKey.getLetter().getOffset() + actualKey.getAccidental().map(a => a.getOffset()).orElse(0))) / MusicConstants_1.default.KEYS_IN_OCTAVE;
            return Exceptional_1.default.of(new Pitch(actualKey, octave));
        })
            .get(a => a[0]);
    }
    getProgramNumber() {
        return this.octave * MusicConstants_1.default.KEYS_IN_OCTAVE + this.key.getLetter().getOffset() + this.key.getAccidental().map(a => a.getOffset()).orElse(0);
    }
    /**
     * Any input in the form
     *   - ${key}${octave}
     *   - ${key}${octave}(${program number})
     * is accepted and converted into a Pitch.
     * ${program number} is intentionally not accepted because #fromProgramNumber
     * exists and should be used instead.
     */
    static fromString(pitchString) {
        return EtudeParser_1.default
            .of(pitchString)
            .filter(o => o != null, EtudeError_1.default.forNull(Pitch))
            .parse(s => {
            // longest prefix that contains only letters or #
            const keyString = s.match(/^[a-zA-Z#]*/g)[0];
            return Key_1.default.fromString(keyString);
        })
            .parse(s => Exceptional_1.default
            .of(s.match("\\d+(?![^(]*\\))")[0], EtudeError_1.default.forInvalid(Pitch, pitchString, "doesn't contain a valid octave"))
            .map(parseInt))
            .get(a => new Pitch(a[0], a[1]))
            .filter(p => {
            // a number that has an open parentheses somewhere before it
            const programNumber = pitchString.match(/\((\d+)/g);
            return programNumber == null || p.getProgramNumber() == parseInt(programNumber[0].substring(0));
        }, EtudeError_1.default.forInvalid(Pitch, pitchString, "program number doesn't match key and octave"))
            .filter(p => {
            let converted = p.toString();
            const programNumber = pitchString.match(/\((\d+)/g);
            if (programNumber == null) {
                converted = converted.substring(0, converted.indexOf("("));
            }
            return converted.localeCompare(pitchString) === 0;
        }, EtudeError_1.default.forInvalid(Pitch, pitchString));
    }
    toString() {
        return this.key.toString() + this.octave + "(" + this.getProgramNumber() + ")";
    }
    equals(other) {
        if (!(other instanceof Pitch)) {
            return false;
        }
        if (other === this) {
            return true;
        }
        let otherPitch = other;
        return this.key.equals(otherPitch.getKey()) && this.octave === otherPitch.getOctave();
    }
    getKey() {
        return this.key;
    }
    getOctave() {
        return this.octave;
    }
}
exports.default = Pitch;
